import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiService from "../../services/api";
import Toast from "../../components/Toast";
import dayjs from "dayjs";

interface Message {
  id: string;
  message_type: string;
  subject?: string;
  content: string;
  sender_type: string;
  sender_name: string;
  sender_email: string;
  status: string;
  sent_at: string;
  read_at?: string;
  email_sent: boolean;
  created_at: string;
}

interface ConversationInfo {
  id: string;
  subject: string;
  job_seeker_email: string;
  job_seeker_has_account: boolean;
  status: string;
  priority?: string;
  sharing_type?: string;
  is_private?: boolean;
  owner?: {
    id: string;
    name: string;
    email: string;
  };
  created_at: string;
}

interface ConversationResponse {
  messages: Message[];
  total: number;
  conversation: ConversationInfo;
}

const ConversationDetail: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<Message[]>([]);
  const [conversation, setConversation] = useState<ConversationInfo | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [replySubject, setReplySubject] = useState("");
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "warning" | "info";
    isVisible: boolean;
  }>({
    message: "",
    type: "success",
    isVisible: false,
  });

  useEffect(() => {
    if (conversationId) {
      loadConversation();
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadConversation = async () => {
    if (!conversationId) return;

    try {
      setLoading(true);
      const response: ConversationResponse =
        await apiService.getConversationMessages(conversationId);
      setMessages(response.messages);
      setConversation(response.conversation);

      // Set default reply subject
      if (response.conversation && !replySubject) {
        const subject = response.conversation.subject;
        setReplySubject(
          subject.startsWith("Re: ") ? subject : `Re: ${subject}`,
        );
      }
    } catch (error) {
      console.error("Failed to load conversation:", error);
      setToast({
        message: "Failed to load conversation. Please try again.",
        type: "error",
        isVisible: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendReply = async () => {
    if (!conversationId || !replyContent.trim()) return;

    try {
      setSendingMessage(true);
      await apiService.sendMessageToConversation(conversationId, {
        subject: replySubject,
        content: replyContent,
        message_type: "reply",
      });

      setToast({
        message: "Message sent successfully!",
        type: "success",
        isVisible: true,
      });

      setReplyContent("");
      await loadConversation(); // Reload to get the new message
    } catch (error) {
      console.error("Failed to send message:", error);
      setToast({
        message: "Failed to send message. Please try again.",
        type: "error",
        isVisible: true,
      });
    } finally {
      setSendingMessage(false);
    }
  };
  const formatTimeAgo = (date?: string, format?: string) => {
    if (!date || date.trim() === "") return "";

    try {
      const utcDate = date.endsWith("Z") ? date : `${date}Z`;

      const localDate = dayjs.utc(utcDate).local();

      return format
        ? localDate.format(format) 
        : localDate.fromNow(); 
    } catch (e) {
      console.error("Date error:", e);
      return "";
    }
  };
  // const formatTimeAgo = (dateString: string) => {
  //   const date = new Date(dateString);
  //   const now = new Date();
  //   const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  //   if (diffInSeconds < 60) return 'Just now';
  //   if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  //   if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  //   if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;

  //   return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  // };

  const closeToast = () => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  };

  if (loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                    <div className="h-16 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="p-6 max-w-4xl mx-auto text-center">
        <div className="text-6xl mb-4">❌</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Conversation not found
        </h2>
        <p className="text-gray-600 mb-6">
          The conversation you're looking for doesn't exist or you don't have
          access to it.
        </p>
        <button
          onClick={() => navigate("/company/messages")}
          className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors"
        >
          Back to Messages
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate("/company/messages")}
              className="text-indigo-600 hover:text-indigo-700 mb-2 flex items-center"
            >
              ← Back to Messages
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              {conversation.subject}
            </h1>
            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
              <span className="flex items-center">
                <span className="mr-1">📧</span>
                {conversation.job_seeker_email}
              </span>
              <span className="flex items-center">
                {conversation.job_seeker_has_account ? (
                  <>
                    <span className="mr-1">✅</span>
                    Active User
                  </>
                ) : (
                  <>
                    <span className="mr-1">📤</span>
                    Invited to Join
                  </>
                )}
              </span>
              <span>Started {formatTimeAgo(conversation.created_at)}</span>
              {conversation.owner && (
                <span className="flex items-center">
                  <span className="mr-1">👤</span>
                  Owner: {conversation.owner.name}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${
              message.sender_type === "company"
                ? "justify-end"
                : "justify-start"
            }`}
          >
            <div
              className={`max-w-xs lg:max-w-md px-4 py-3 rounded-lg ${
                message.sender_type === "company"
                  ? "bg-indigo-600 text-white"
                  : "bg-white border border-gray-200"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span
                  className={`text-sm font-medium ${
                    message.sender_type === "company"
                      ? "text-indigo-100"
                      : "text-gray-700"
                  }`}
                >
                  {message.sender_name}
                </span>
                <span
                  className={`text-xs ${
                    message.sender_type === "company"
                      ? "text-indigo-200"
                      : "text-gray-500"
                  }`}
                >
                  {formatTimeAgo(message.sent_at)}
                </span>
              </div>

              {message.subject &&
                message.message_type === "initial_contact" && (
                  <div
                    className={`text-sm mb-2 font-medium ${
                      message.sender_type === "company"
                        ? "text-indigo-100"
                        : "text-gray-600"
                    }`}
                  >
                    Subject: {message.subject}
                  </div>
                )}

              <div
                className={`text-sm whitespace-pre-wrap ${
                  message.sender_type === "company"
                    ? "text-white"
                    : "text-gray-800"
                }`}
              >
                {message.content}
              </div>

              {message.sender_type === "company" && (
                <div className="flex items-center justify-end mt-2">
                  <span className="text-xs text-indigo-200">
                    {message.status}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Send Reply</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject
            </label>
            <input
              type="text"
              value={replySubject}
              onChange={(e) => setReplySubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Reply subject..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message
            </label>
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Type your reply here..."
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {conversation.job_seeker_has_account ? (
                <span className="flex items-center">
                  <span className="mr-1">✅</span>
                  Recipient will be notified via email and in-app notification
                </span>
              ) : (
                <span className="flex items-center">
                  <span className="mr-1">📧</span>
                  Recipient will be notified via email with account creation
                  link
                </span>
              )}
            </div>

            <button
              onClick={handleSendReply}
              disabled={!replyContent.trim() || sendingMessage}
              className="bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sendingMessage ? "Sending..." : "Send Reply"}
            </button>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={closeToast}
        />
      )}
    </div>
  );
};

export default ConversationDetail;
